import { Box, Divider, Stack, Typography } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect } from "react";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import TextButton from "@/components/common/buttons/textButton";
import { EditTemplateIcon } from "@/components/common/icons";
import InfoCard from "@/components/common/infoCard/infoCard";
import { GREY } from "@/config/mui/colorPalette";
import useErrorLogger from "@/utils/useErrorLogger";
import AppointmentCheckInRow from "./appointmentCheckInRow";
import AppointmentConfirmRow from "./appointmentConfirmRow";
import { Appointment } from "./appointmentUpdateStatusRow";

type Props = {
  toCheckIn: Appointment[];
  isCheckingInAppointment: boolean;
  checkInAppointmentAction: (appointmentAccessToken: string) => void;
  toConfirm: Appointment[];
  unsignedForms: {
    name: string;
    id: string;
    url: string;
  }[];
  isConfirmingAppointment: boolean;
  confirmAppointmentAction: (appointmentAccessToken: string) => void;
  isAppointmentSpecific?: boolean;
  clientAccessToken?: string;
};

export default function OutstandingItemsSection({
  toCheckIn,
  isCheckingInAppointment,
  checkInAppointmentAction,
  toConfirm,
  unsignedForms,
  isConfirmingAppointment,
  confirmAppointmentAction,
  isAppointmentSpecific = false,
}: Props) {
  return (
    <Stack
      spacing={1}
      sx={{
        mb: 3,
      }}
    >
      <AdditionalInfo
        variant="warning"
        text="To ensure you can be treated at your appointment, complete the action items below."
      />
      <OutstandingItemsCard>
        {toCheckIn.map((appointment) => (
          <AppointmentCheckInRow
            key={appointment.id}
            appointment={appointment}
            isCheckingInAppointment={isCheckingInAppointment}
            checkInAppointmentAction={checkInAppointmentAction}
          />
        ))}
        {unsignedForms.map((form) => (
          <FormRow
            key={form.url}
            url={form.url}
            isConfirmingAppointment={isConfirmingAppointment}
            name={form.name}
            isAppointmentSpecific={isAppointmentSpecific}
          />
        ))}
        {toConfirm.map((appointment) => (
          <AppointmentConfirmRow
            key={appointment.id}
            appointment={appointment}
            isConfirmingAppointment={isConfirmingAppointment}
            confirmAppointmentAction={confirmAppointmentAction}
            isAppointmentSpecific={isAppointmentSpecific}
          />
        ))}
      </OutstandingItemsCard>
    </Stack>
  );
}

export function OutstandingItemsCard({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <InfoCard sx={{ "& .MuiCardContent-root": { py: "0 !important" } }}>
      <Stack divider={<Divider />}>{children}</Stack>
    </InfoCard>
  );
}

export function FormRow({
  url,
  name,
  isConfirmingAppointment,
  isAppointmentSpecific,
}: {
  url: string;
  name: string;
  isConfirmingAppointment: boolean;
  isAppointmentSpecific: boolean;
}) {
  const logError = useErrorLogger();

  const {
    query: { appointmentAccessToken, medspaSlug },
  } = useRouter();

  let href = "";

  useEffect(() => {
    if (isAppointmentSpecific && !appointmentAccessToken) {
      logError(
        "The appointmentAccessToken is missing and could cause wrong navigation behaviors on appointment specific forms."
      );
    }
  }, [isAppointmentSpecific, appointmentAccessToken, logError]);

  if (isAppointmentSpecific && appointmentAccessToken) {
    href = `/client/${medspaSlug}/appointments/${appointmentAccessToken}/${url}`;
  } else {
    href = `/client/${medspaSlug}/${url}`;
  }

  return (
    <Stack
      direction="row"
      key={href}
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <Box>
          <EditTemplateIcon color={GREY[40]} />
        </Box>
        <Typography
          variant="paragraphSmall"
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
      </Stack>
      <TextButton
        href={href}
        disabled={isConfirmingAppointment}
        sx={{ minWidth: "fit-content" }}
      >
        Complete form
      </TextButton>
    </Stack>
  );
}
